/**
 * Coupon code tracking — data layer (feature #171).
 *
 * Thin typed wrappers over `apiClient` for the surface at
 * /api/v1/coupon-tracking. No React / TanStack imports so the module can be
 * unit-tested without a DOM.
 *
 * Three attribution modes:
 *   general   — shared pool; any publisher can drive attribution with the
 *               code. The caller of /attribute supplies the publisher_id.
 *   exclusive — reserved for one publisher from creation.
 *   unique    — one-time-use code; 409 on second /attribute.
 *
 * Endpoints wrapped:
 *   GET    /coupon-tracking?campaign_id=        (listCoupons)
 *   POST   /coupon-tracking                     (createCoupon)
 *   PATCH  /coupon-tracking/:id                 (updateCoupon)
 *   DELETE /coupon-tracking/:id                 (deleteCoupon, soft-delete)
 *   POST   /coupon-tracking/generate            (generateUniqueBatch)
 *   POST   /coupon-tracking/attribute           (attributeCoupon)
 */

import { apiClient } from "@/api-client";

// ---------------------------------------------------------------------------
// Row shapes — mirror the API response.
// ---------------------------------------------------------------------------

export const COUPON_MODES = ["general", "exclusive", "unique"] as const;
export type CouponMode = (typeof COUPON_MODES)[number];

export interface Coupon {
	id: string;
	tenant_id: string;
	campaign_id: string;
	code: string;
	mode: CouponMode;
	publisher_id: string | null;
	usage_cap: number | null;
	usage_count: number;
	active: number; // 1 or 0
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

export interface CouponAttribution {
	id: string;
	tenant_id: string;
	coupon_id: string;
	click_id: string;
	created_at: number;
}

// ---------------------------------------------------------------------------
// Validators — mirror the server contract so the UI can short-circuit the
// network round-trip on obviously bad input.
// ---------------------------------------------------------------------------

const ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;
const CODE_RE = /^[A-Z0-9._-]{3,64}$/;

export function validateId(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (!ID_RE.test(trimmed)) return null;
	return trimmed;
}

/**
 * Trim + upper-case a coupon code. Returns null on shape failure. Keep in
 * sync with `normalizeCode` in the API route.
 */
export function normalizeCode(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const upper = raw.trim().toUpperCase();
	if (!CODE_RE.test(upper)) return null;
	return upper;
}

export function validateMode(raw: string): CouponMode | null {
	if (typeof raw !== "string") return null;
	if ((COUPON_MODES as readonly string[]).includes(raw)) {
		return raw as CouponMode;
	}
	return null;
}

/**
 * Positive integer cap (or null). 0 is not a valid cap — use null to mean
 * "unlimited".
 */
export function validateUsageCap(raw: number | string | null | undefined):
	| number
	| null
	| "invalid" {
	if (raw === null || raw === undefined || raw === "") return null;
	const n = typeof raw === "string" ? Number(raw) : raw;
	if (!Number.isFinite(n) || !Number.isInteger(n)) return "invalid";
	if (n < 1 || n > 10_000_000) return "invalid";
	return n;
}

/** 1..500 integer. */
export function validateGenerateCount(raw: number | string):
	| number
	| "invalid" {
	const n = typeof raw === "string" ? Number(raw) : raw;
	if (!Number.isFinite(n) || !Number.isInteger(n)) return "invalid";
	if (n < 1 || n > 500) return "invalid";
	return n;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatUnixSec(unixSec: number | null | undefined): string {
	if (unixSec == null) return "—";
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export function formatUsage(coupon: Coupon): string {
	if (coupon.usage_cap == null) return `${coupon.usage_count} / ∞`;
	return `${coupon.usage_count} / ${coupon.usage_cap}`;
}

export type CouponStatus = "deleted" | "inactive" | "exhausted" | "active";

export function statusOf(coupon: Coupon): CouponStatus {
	if (coupon.deleted_at !== null) return "deleted";
	if (coupon.active !== 1) return "inactive";
	if (coupon.mode === "unique" && coupon.usage_count >= 1) return "exhausted";
	if (
		typeof coupon.usage_cap === "number" &&
		coupon.usage_count >= coupon.usage_cap
	) {
		return "exhausted";
	}
	return "active";
}

export function modeBadgeClass(mode: CouponMode): string {
	switch (mode) {
		case "general":
			return "inline-flex items-center gap-1 rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700";
		case "exclusive":
			return "inline-flex items-center gap-1 rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700";
		case "unique":
			return "inline-flex items-center gap-1 rounded-[6px] border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700";
	}
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const couponTrackingQueryKeys = {
	all: ["coupon-tracking"] as const,
	listByCampaign: (campaignId: string) =>
		[...couponTrackingQueryKeys.all, "list", campaignId] as const,
};

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/coupon-tracking";

export interface ListCouponsResponse {
	coupons: Coupon[];
}

export async function listCoupons(campaignId: string): Promise<Coupon[]> {
	const res = await apiClient.get<ListCouponsResponse>(BASE, {
		query: { campaign_id: campaignId },
	});
	return res.coupons;
}

export interface CreateCouponArgs {
	campaign_id: string;
	mode: CouponMode;
	code: string;
	publisher_id?: string | null;
	usage_cap?: number | null;
}

export interface CouponResponse {
	coupon: Coupon;
}

export async function createCoupon(
	args: CreateCouponArgs,
): Promise<Coupon> {
	const payload: Record<string, unknown> = {
		campaign_id: args.campaign_id,
		mode: args.mode,
		code: args.code,
	};
	if (args.publisher_id !== undefined && args.publisher_id !== null) {
		payload.publisher_id = args.publisher_id;
	}
	if (args.usage_cap !== undefined && args.usage_cap !== null) {
		payload.usage_cap = args.usage_cap;
	}
	const res = await apiClient.post<CouponResponse>(BASE, payload);
	return res.coupon;
}

export interface UpdateCouponArgs {
	id: string;
	active?: boolean;
	usage_cap?: number | null;
	publisher_id?: string | null;
}

export async function updateCoupon(
	args: UpdateCouponArgs,
): Promise<Coupon> {
	const payload: Record<string, unknown> = {};
	if (args.active !== undefined) payload.active = args.active;
	if (args.usage_cap !== undefined) payload.usage_cap = args.usage_cap;
	if (args.publisher_id !== undefined) {
		payload.publisher_id = args.publisher_id;
	}
	const res = await apiClient.patch<CouponResponse>(
		`${BASE}/${encodeURIComponent(args.id)}`,
		payload,
	);
	return res.coupon;
}

export async function deleteCoupon(
	id: string,
): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/${encodeURIComponent(id)}`,
	);
}

export interface GenerateBatchArgs {
	campaign_id: string;
	publisher_id: string;
	count: number;
	usage_cap?: number | null;
	prefix?: string;
	length?: number;
}

export interface GenerateBatchResponse {
	count: number;
	coupons: Coupon[];
}

export async function generateBatch(
	args: GenerateBatchArgs,
): Promise<GenerateBatchResponse> {
	const payload: Record<string, unknown> = {
		campaign_id: args.campaign_id,
		publisher_id: args.publisher_id,
		count: args.count,
	};
	if (args.usage_cap !== undefined && args.usage_cap !== null) {
		payload.usage_cap = args.usage_cap;
	}
	if (args.prefix !== undefined) payload.prefix = args.prefix;
	if (args.length !== undefined) payload.length = args.length;
	return apiClient.post<GenerateBatchResponse>(`${BASE}/generate`, payload);
}

export interface AttributeCouponArgs {
	campaign_id: string;
	code: string;
	click_id: string;
	publisher_id?: string;
}

export interface AttributeCouponResponse {
	attribution: CouponAttribution;
	coupon: Coupon;
	publisher_id: string;
}

export async function attributeCoupon(
	args: AttributeCouponArgs,
): Promise<AttributeCouponResponse> {
	const payload: Record<string, unknown> = {
		campaign_id: args.campaign_id,
		code: args.code,
		click_id: args.click_id,
	};
	if (args.publisher_id !== undefined) {
		payload.publisher_id = args.publisher_id;
	}
	return apiClient.post<AttributeCouponResponse>(`${BASE}/attribute`, payload);
}
